import { head, last, flatMap, sortBy, orderBy, groupBy, remove } from 'lodash';
import { observable, computed, runInAction, makeObservable } from 'mobx';
import {
    object,
    list,
    Clazz,
    custom,
    update,
    PropSchema,
    AdditionalPropArgs,
    SKIP,
    createModelSchema,
    ModelSchema
} from 'serializr';
import Context from 'serializr/lib/core/Context';

interface IndexItem {
    ix: number | null;
}

export function indexListSchema<T extends IndexItem>(Type: Clazz<T>): ModelSchema<IndexList<T>> {
    return createModelSchema<IndexList<T>>(
        IndexList,
        {
            items: custom(
                (...args) => list(object(Type)).serializer(...args),
                (jsonItems: IndexItem[], context, _, callback) => {
                    runInAction(() => {
                        const target = context.target as IndexList<T>;
                        const { type } = context.json as IndexList<T>;
                        switch (type) {
                            case 'delete':
                                // Remove in reverse to prevent messing up indexes of subsequent deletions
                                jsonItems.reverse().forEach((item) => {
                                    item.ix && target.items.splice(item.ix, 1);
                                });
                                callback(null, SKIP);
                                break;
                            case 'update':
                                jsonItems.forEach((item) => {
                                    const itemToUpdate = item.ix != null && target.items[item.ix];
                                    if (itemToUpdate) {
                                        update(itemToUpdate, item);
                                    }
                                });
                                callback(null, SKIP);
                                break;
                            case 'create':
                                list(object(Type)).deserializer(
                                    jsonItems,
                                    (err, newItems?: T[]) => {
                                        if (err) {
                                            callback(err, undefined);
                                        } else {
                                            newItems?.forEach((item) => {
                                                if (item.ix) target.items[item.ix] = item;
                                            });
                                            callback(null, SKIP);
                                        }
                                    },
                                    context
                                );
                                break;
                            case 'load':
                            default:
                                list(object(Type)).deserializer(
                                    jsonItems,
                                    (err, newItems: T[]) => {
                                        if (err) {
                                            callback(err, undefined);
                                        } else {
                                            target.items.replace(newItems);
                                            callback(null, SKIP);
                                        }
                                    },
                                    context
                                );
                                break;
                        }
                    });
                }
            )
        },
        () => new IndexList(Type)
    );
}

export function indexList<T extends IndexItem>(
    Type: Clazz<T>,
    additionalArgs?: AdditionalPropArgs
): PropSchema {
    const modelSchema = indexListSchema(Type);
    return custom(
        object(modelSchema).serializer,
        (
            jsonValue: IndexList | undefined,
            context,
            target: IndexList<T> | undefined | null,
            callback
        ) => {
            if (!jsonValue) {
                return callback(null, jsonValue);
            }
            if (!target) {
                target = modelSchema.factory(context);
            }
            const { deserializer } = modelSchema.props.items as PropSchema;
            deserializer(jsonValue.items, (err) => callback(err, target), {
                ...context,
                target,
                parentContext: context,
                json: jsonValue
            } as Context);
        },
        additionalArgs
    );
}

export class IndexList<T extends IndexItem = any> {
    serializeInfo: any;

    items = observable.array<T>();
    type = 'load';

    forEach: this['items']['forEach'] = this.items.forEach.bind(this.items);
    map: this['items']['map'] = this.items.map.bind(this.items);
    reduce: this['items']['reduce'] = this.items.reduce.bind(this.items);
    slice: this['items']['slice'] = this.items.slice.bind(this.items);
    splice: this['items']['splice'] = this.items.splice.bind(this.items);
    find: this['items']['find'] = this.items.find.bind(this.items);
    filter: this['items']['filter'] = this.items.filter.bind(this.items);
    push: this['items']['push'] = this.items.push.bind(this.items);
    unshift: this['items']['unshift'] = this.items.unshift.bind(this.items);

    first = (): T | undefined => head(this.items);
    last = (): T | undefined => last(this.items);
    remove = (predicate: T): T[] => remove(this.items, predicate);
    flatMap = (iteratee: T): boolean[] => flatMap(this.items, iteratee);
    sortBy = (...iteratees: T[]): T[] => sortBy(this.items, ...iteratees);
    orderBy = (iteratees: T[], orders?: (boolean | 'asc' | 'desc')[]): T[] =>
        orderBy(this.items, iteratees, orders);

    constructor(Type: Clazz<T>) {
        makeObservable(this, {
            items: observable,
            length: computed
        });

        this.serializeInfo = indexListSchema(Type);
    }

    get length(): number {
        return this.items.length;
    }
}
